import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Breadcrumb,
  Layout,
  theme,
  Row,
  Col,
  Card,
  Avatar,
  Typography,
  Tag,
  Button,
  Input,
  Space,
  Spin,
  Alert,
  Divider,
  Tooltip,
  Badge,
  Empty,
  message,
  Drawer,
  Select,
  Modal,
  Radio
} from "antd";
import {
  SendOutlined,
  UserOutlined,
  RobotOutlined,
  BookOutlined,
  BranchesOutlined,
  AimOutlined,
  BarChartOutlined,
  BankOutlined,
  MessageOutlined,
  HistoryOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useTierAccess } from "../hooks/useTierAccess";
import { useSearchParams } from "react-router-dom";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { CrownOutlined, LockOutlined } from "@ant-design/icons";
import { useStreamingChat, type ExpertChatScope } from "../hooks/useStreamingChat";
import {
  useGetAvailableExpertsQuery,
  useGetUserConversationsQuery,
  useGetAIConversationQuery,
  useCreateAIConversationMutation,
  useDeleteAIConversationMutation,
  useGetBrandsQuery,
  useCheckExpertAccessMutation,
  useGetOrganisationEntitlementsQuery,
  IAIExpert,
  IAIConversation,
} from "../services/nestjs-base-api";
import SubscriptionUpgradeModal from "../components/SubscriptionUpgradeModal";
import { SubscriptionPlanEnum } from "../services/nestjs-base-api/types";
import { MaturityTag } from "../components/MaturityTag";
import { MaturityAlert } from "../components/MaturityAlert";
import { escapeHtml, sanitizeHtml } from "../utils/sanitize";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Icon mapping
const iconMap: { [key: string]: React.ReactNode } = {
  BookOpen: <BookOutlined />,
  GraduationCap: <BookOutlined />,
  Megaphone: <MessageOutlined />,
  Brain: <BranchesOutlined />,
  Target: <AimOutlined />,
  ChartBar: <BarChartOutlined />,
  Briefcase: <BankOutlined />,
};

// Color mapping
const colorMap: { [key: string]: string } = {
  'bg-blue-500': '#3b82f6',
  'bg-purple-500': '#8b5cf6',
  'bg-green-500': '#10b981',
  'bg-orange-500': '#f97316',
  'bg-red-500': '#ef4444',
  'bg-pink-500': '#ec4899',
  'bg-indigo-500': '#6366f1',
  'bg-yellow-500': '#eab308',
};

// CSS for typing animation
const typingAnimationStyle = `
  @keyframes typing {
    0% {
      transform: translateY(0);
      opacity: 0.5;
    }
    50% {
      transform: translateY(-10px);
      opacity: 1;
    }
    100% {
      transform: translateY(0);
      opacity: 0.5;
    }
  }
`;

// Credit Indicator Component
interface CreditIndicatorProps {
  remaining: number;
  limit: number;
  isLow: boolean;
  isOut: boolean;
  onBuyMore?: () => void;
}

const CreditIndicator: React.FC<CreditIndicatorProps> = ({ remaining, limit, isLow, isOut, onBuyMore }) => {
  if (limit <= 0) return null; // Don't show for unlimited or no access

  const getColor = () => {
    if (isOut) return '#ff4d4f';
    if (isLow) return '#faad14';
    return '#F59E0B';
  };

  return (
    <Tooltip title={isOut ? "You've used all your AI Expert credits" : `${remaining} of ${limit} credits remaining`}>
      <Tag
        color={isOut ? 'error' : isLow ? 'warning' : 'default'}
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4
        }}
        onClick={onBuyMore}
      >
        <ThunderboltOutlined style={{ color: getColor() }} />
        <span style={{ color: getColor(), fontWeight: 500 }}>
          {remaining} / {limit}
        </span>
      </Tag>
    </Tooltip>
  );
};

interface AIExpertPageProps {
  /** Embedded in the Marketer View shell: hide page chrome + the expert grid. */
  embedded?: boolean;
  /** Auto-select this expert (by expertType OR name) and skip the grid. */
  forcedExpertType?: string;
  /** Fixed brand id when embedded (replaces the page's brand picker). */
  brandId?: number;
  /** Data-selection scope (period/cohorts/countries) the chat should reason over. */
  scope?: ExpertChatScope;
}

const AIExpertPage: React.FunctionComponent<AIExpertPageProps> = ({
  embedded = false,
  forcedExpertType,
  brandId: brandIdProp,
  scope,
}) => {
  const { user } = useAuth();
  const intl = useIntl();
  const navigate = useScopedNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAiExpertsAccess, isLoading: tierLoading } = useTierAccess();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // URL parameters for deep linking
  const expertParam = searchParams.get('expert');
  const promptParam = searchParams.get('prompt');
  const contextParam = searchParams.get('context');

  // State
  const [selectedBrandId, setSelectedBrandId] = useState<number>();
  const [selectedExpert, setSelectedExpert] = useState<IAIExpert | null>(null);
  const [currentConversation, setCurrentConversation] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [conversationsDrawerOpen, setConversationsDrawerOpen] = useState(false);
  const [localMessages, setLocalMessages] = useState<any[]>([]);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState<{
    requiredPlan: SubscriptionPlanEnum;
    currentPlan?: SubscriptionPlanEnum;
    expertName: string;
  } | null>(null);
  const [processedExpertParam, setProcessedExpertParam] = useState<string | null>(null);
  const [pendingAutoStartConversation, setPendingAutoStartConversation] = useState(false);
  const [responseStyle, setResponseStyle] = useState<'brief' | 'detailed'>('brief');

  // Load saved responseStyle from localStorage when expert changes
  useEffect(() => {
    if (selectedExpert) {
      const saved = localStorage.getItem(`ai-expert-responseStyle-${selectedExpert.expertType}`);
      if (saved === 'brief' || saved === 'detailed') {
        setResponseStyle(saved);
      }
    }
  }, [selectedExpert]);

  const handleResponseStyleChange = (value: 'brief' | 'detailed') => {
    setResponseStyle(value);
    if (selectedExpert) {
      localStorage.setItem(`ai-expert-responseStyle-${selectedExpert.expertType}`, value);
    }
  };
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // API Hooks
  const { data: brandsData } = useGetBrandsQuery(
    { jwtToken: user!.jwtToken, organisationId: user!.organisation.id },
    { skip: !user?.jwtToken }
  );

  const brands = useMemo(() => brandsData || [], [brandsData]);
  const selectedBrand = brands.find(b => b.id === selectedBrandId);

  useEffect(() => {
    // Embedded: brand is fixed via prop (the Marketer View scope brand).
    if (embedded && brandIdProp) {
      setSelectedBrandId((prev) => (prev === brandIdProp ? prev : brandIdProp));
      return;
    }
    if (brands.length > 0 && !selectedBrandId) {
      setSelectedBrandId(brands[0].id);
    }
  }, [embedded, brandIdProp, brands, selectedBrandId]);

  const {
    data: experts,
    isLoading: expertsLoading,
    error: expertsError
  } = useGetAvailableExpertsQuery({
    jwtToken: user?.jwtToken || '',
    brandId: selectedBrandId,
  }, {
    skip: !user?.jwtToken
  });

  // Embedded: auto-select the forced expert (by expertType OR name) and skip
  // the grid; fall back to the first available expert if not found.
  useEffect(() => {
    if (!embedded || !forcedExpertType || selectedExpert || !experts || experts.length === 0) {
      return;
    }
    const norm = (s: string) => s.toLowerCase().replace(/[-_\s]/g, '');
    const target = norm(forcedExpertType);
    const match =
      experts.find((e) => norm(e.expertType) === target || norm(e.name) === target) ??
      experts[0];
    if (match) setSelectedExpert(match);
  }, [embedded, forcedExpertType, experts, selectedExpert]);

  const {
    data: conversations,
    isLoading: conversationsLoading,
    refetch: refetchConversations
  } = useGetUserConversationsQuery({
    jwtToken: user?.jwtToken || '',
    expertType: selectedExpert?.expertType
  }, {
    skip: !selectedExpert
  });

  const {
    data: conversationData,
    isLoading: messagesLoading,
    refetch: refetchMessages
  } = useGetAIConversationQuery({
    jwtToken: user?.jwtToken || '',
    conversationId: currentConversation || ''
  }, {
    skip: !currentConversation
  });

  // Fetch entitlements for AI Expert Credits display
  // Poll every 30 seconds to catch updates from other users in same org
  const { data: entitlements, refetch: refetchEntitlements } = useGetOrganisationEntitlementsQuery({
    jwtToken: user?.jwtToken || '',
    orgId: String(user?.organisation?.id || ''),
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
    pollingInterval: 30000, // 30 seconds
  });

  // Get AI Expert Credits from entitlements
  const aiCreditsEntitlement = entitlements?.find(e => e.feature === 'AI_EXPERT_CREDITS_MONTHLY');
  const creditsRemaining = aiCreditsEntitlement?.remaining ?? aiCreditsEntitlement?.limit ?? 0;
  const creditsLimit = aiCreditsEntitlement?.limit ?? 0;
  const isLowCredits = creditsLimit > 0 && creditsRemaining <= 5;
  const isOutOfCredits = creditsLimit > 0 && creditsRemaining <= 0;

  const [createConversation] = useCreateAIConversationMutation();
  const [deleteConversation] = useDeleteAIConversationMutation();
  const [checkExpertAccess] = useCheckExpertAccessMutation();

  // Streaming chat hook
  const { streamMessage, streamingContent, isStreaming } = useStreamingChat();

  // Sync local messages with conversation data
  useEffect(() => {
    if (conversationData?.messages) {
      setLocalMessages(conversationData.messages);
      setIsWaitingForResponse(false);
    }
  }, [conversationData?.messages]);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages, isWaitingForResponse, isStreaming, streamingContent]);

  // URL Parameter Handling: Auto-select expert from URL param
  useEffect(() => {
    // Skip if no expert param, already processed this exact param, or still loading
    if (!expertParam || processedExpertParam === expertParam || !experts || expertsLoading || tierLoading) return;

    // Normalize function: lowercase, remove dashes/spaces/underscores
    const normalize = (str: string) => str.toLowerCase().replace(/[-_\s]/g, '');
    const normalizedParam = normalize(expertParam);

    // Find matching expert by expertType OR name (both normalized)
    const matchingExpert = experts.find(
      e => e.isActive && (
        normalize(e.expertType) === normalizedParam ||
        normalize(e.name) === normalizedParam
      )
    );

    if (matchingExpert) {
      // Use the existing handleExpertSelect logic (but async)
      (async () => {
        // Block access for users without AI Experts tier access (e.g. Free plan)
        if (!tierLoading && !hasAiExpertsAccess) {
          setProcessedExpertParam(expertParam);
          setSearchParams({}, { replace: true });
          navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
          return;
        }

        // Check subscription access if required
        if (matchingExpert.requiredSubscriptionLevel && selectedBrandId) {
          try {
            const result = await checkExpertAccess({
              jwtToken: user?.jwtToken || '',
              expertType: matchingExpert.expertType,
              brandId: selectedBrandId
            }).unwrap();

            if (!result.hasAccess) {
              // Show upgrade modal
              setUpgradeInfo({
                requiredPlan: result.requiredSubscriptionLevel as SubscriptionPlanEnum,
                currentPlan: result.currentSubscriptionLevel as SubscriptionPlanEnum,
                expertName: matchingExpert.name,
              });
              setShowUpgradeModal(true);
              setProcessedExpertParam(expertParam);
              // Clear URL params (replace to avoid extra history entry)
              setSearchParams({}, { replace: true });
              return;
            }
          } catch {
            message.error('Failed to check expert access');
            setProcessedExpertParam(expertParam);
            setSearchParams({}, { replace: true });
            return;
          }
        }

        // Access granted, select the expert
        setSelectedExpert(matchingExpert);
        setCurrentConversation(null);
        setLocalMessages([]);
        setIsWaitingForResponse(false);
        setProcessedExpertParam(expertParam);
      })();
    } else {
      // Expert not found - show notification
      message.warning(`Expert "${expertParam}" not found`);
      setProcessedExpertParam(expertParam);
      // Clear URL params (replace to avoid extra history entry)
      setSearchParams({}, { replace: true });
    }
  }, [experts, expertParam, processedExpertParam, expertsLoading, selectedBrandId, user?.jwtToken, checkExpertAccess, setSearchParams, hasAiExpertsAccess, tierLoading, navigate]);

  // URL Parameter Handling: Set flag to auto-create conversation when expert selected via URL with prompt
  useEffect(() => {
    // The expert either arrived via ?expert= or is forced by the embedded
    // Marketer View shell — in both cases a ?prompt= deep link should
    // auto-start a conversation so the composer can be pre-filled.
    const expertResolved = !!processedExpertParam || (embedded && !!forcedExpertType);
    if (selectedExpert && promptParam && !currentConversation && expertResolved && !expertsLoading) {
      setPendingAutoStartConversation(true);
    }
  }, [selectedExpert, promptParam, currentConversation, processedExpertParam, expertsLoading, embedded, forcedExpertType]);

  // URL Parameter Handling: Pre-fill prompt after conversation is created
  useEffect(() => {
    // Pre-fill prompt when conversation is ready and prompt param exists
    if (currentConversation && promptParam && !messageInput) {
      try {
        setMessageInput(decodeURIComponent(promptParam));
      } catch {
        // searchParams.get() already decodes, so use the value directly if decoding fails
        setMessageInput(promptParam);
      }
      // Clear the URL params after processing (replace to avoid extra history entry)
      setSearchParams({}, { replace: true });
    }
  }, [currentConversation, promptParam, messageInput, setSearchParams]);

  // Memoized formatter for message timestamps
  const formatMessageTime = useCallback((createdAt: string) => {
    return new Date(createdAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const downloadAsMarkdown = useCallback((content: string, messageTime: string) => {
    const filename = `ai-expert-response-${new Date(messageTime).toISOString().split('T')[0]}.md`;
    const header = `# AI Expert Response\n\n**Expert:** ${selectedExpert?.name}\n**Date:** ${new Date(messageTime).toLocaleDateString()}\n**Time:** ${formatMessageTime(messageTime)}\n\n---\n\n`;
    const fullContent = header + content;

    const blob = new Blob([fullContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [selectedExpert, formatMessageTime]);

  const downloadAsPDF = useCallback(async (content: string, messageTime: string) => {
    try {
      // Create a temporary div to render the markdown as HTML
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '800px';
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.color = 'black';

      // Add header. The expert name is admin-configurable in AI Hub, so escape
      // it rather than trusting it into markup.
      const header = `
        <h1>AI Expert Response</h1>
        <p><strong>Expert:</strong> ${escapeHtml(selectedExpert?.name)}</p>
        <p><strong>Date:</strong> ${new Date(messageTime).toLocaleDateString()}</p>
        <p><strong>Time:</strong> ${formatMessageTime(messageTime)}</p>
        <hr>
      `;

      // Convert markdown to HTML (simple approach).
      //
      // Escape FIRST: none of the replacements below escape anything, and this
      // string is AI output that summarises survey data — including open-text
      // answers written by anonymous respondents. It is then assigned via
      // innerHTML AND written into a window opened with window.open(''), which
      // inherits this origin, so unescaped markup would execute with access to
      // localStorage (where the JWT lives).
      const htmlContent = escapeHtml(content)
        .replace(/#{3}\s(.+)/g, '<h3>$1</h3>')
        .replace(/#{2}\s(.+)/g, '<h2>$1</h2>')
        .replace(/#{1}\s(.+)/g, '<h1>$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^\-\s(.+)/gm, '<li>$1</li>')
        .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/^(?!<[h|u|l])/gm, '<p>')
        .replace(/(?![>])$/gm, '</p>');

      // Second layer, in case a future replacement above emits a tag of its own.
      // Everything downstream (including the printWindow document.write) reads
      // back from tempDiv.innerHTML, so sanitising here covers both sinks.
      tempDiv.innerHTML = sanitizeHtml(header + htmlContent);
      document.body.appendChild(tempDiv);

      // Use browser's print functionality to generate PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html lang="en">
            <head>
              <title>AI Expert Response</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1, h2, h3 { color: #333; }
                ul { margin-left: 20px; }
                p { margin: 8px 0; }
              </style>
            </head>
            <body>
              ${tempDiv.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }

      document.body.removeChild(tempDiv);
    } catch (error) {
      message.error('Failed to generate PDF. Please try downloading as markdown instead.');
    }
  }, [selectedExpert, formatMessageTime]);

  // What data selection this conversation is grounded in: stored messages win
  // (revisiting shows what it WAS based on), falling back to the live scope
  // for a conversation with nothing sent yet.
  const scopeStamp = useMemo(() => {
    const stamped = localMessages.find((m: any) => m?.metadata?.scope);
    if (stamped) return stamped.metadata.scope;
    // Live scope only for a conversation with nothing sent yet — an old
    // conversation without a stamp must not be labelled with TODAY's scope.
    return localMessages.length === 0 ? scope : undefined;
  }, [localMessages, scope]);
  const scopeStampText = useMemo(() => {
    if (!scopeStamp) return '';
    const calculated = scopeStamp.calculatedAt
      ? intl.formatMessage(
          { id: 'aiExperts.scopeStamp.calculated', defaultMessage: 'calculated {when}' },
          { when: new Date(scopeStamp.calculatedAt).toLocaleString() },
        )
      : '';
    return [scopeStamp.label, calculated].filter(Boolean).join(' \u00b7 ');
  }, [scopeStamp, intl]);

  // Memoize rendered messages to prevent re-rendering on every keystroke
  const renderedMessages = useMemo(() => {
    return localMessages.map((message: any) => {
      const isUserMessage = message.type === 'user';
      return (
        <div key={message.id} style={{ padding: '8px 0', width: '100%' }}>
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
            alignItems: 'flex-end',
            gap: 8,
            marginLeft: isUserMessage ? 'auto' : '0',
            marginRight: isUserMessage ? '0' : 'auto'
          }}>
            {!isUserMessage && selectedExpert && (
              <Avatar
                size={32}
                style={{
                  backgroundColor: colorMap[selectedExpert.color] || '#F59E0B',
                  flexShrink: 0
                }}
                icon={iconMap[selectedExpert.icon] || <RobotOutlined />}
              />
            )}
            <div style={{
              maxWidth: '70%',
              padding: '12px 16px',
              borderRadius: isUserMessage ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
              backgroundColor: isUserMessage ? '#FEF3C7' : '#f5f5f5',
              border: isUserMessage ? '1px solid rgba(245,158,11,0.35)' : 'none',
              color: isUserMessage ? '#78350F' : '#000',
              position: 'relative'
            }}>
              <div style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                {isUserMessage ? (
                  message.content
                ) : (
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => <h1 style={{ fontSize: '1.2em', fontWeight: 'bold', margin: '8px 0 4px 0' }}>{children}</h1>,
                      h2: ({ children }) => <h2 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '6px 0 3px 0' }}>{children}</h2>,
                      h3: ({ children }) => <h3 style={{ fontSize: '1.05em', fontWeight: 'bold', margin: '4px 0 2px 0' }}>{children}</h3>,
                      p: ({ children }) => <p style={{ margin: '4px 0' }}>{children}</p>,
                      ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
                      strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                      em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                      code: ({ children }) => <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>{children}</code>,
                      blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #ccc', paddingLeft: '12px', margin: '4px 0', fontStyle: 'italic' }}>{children}</blockquote>
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
              </div>
              {!isUserMessage && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>
                    {formatMessageTime(message.createdAt)}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Tooltip title="Download as Markdown">
                      <Button type="text" size="small" icon={<FileTextOutlined />} onClick={() => downloadAsMarkdown(message.content, message.createdAt)} style={{ fontSize: 10, height: 20, width: 20, minWidth: 20, padding: 0, opacity: 0.6 }} />
                    </Tooltip>
                    <Tooltip title="Download as PDF">
                      <Button type="text" size="small" icon={<FilePdfOutlined />} onClick={() => downloadAsPDF(message.content, message.createdAt)} style={{ fontSize: 10, height: 20, width: 20, minWidth: 20, padding: 0, opacity: 0.6 }} />
                    </Tooltip>
                  </div>
                </div>
              )}
              {isUserMessage && (
                <div style={{ fontSize: 11, opacity: 0.7, textAlign: 'right' }}>
                  {formatMessageTime(message.createdAt)}
                </div>
              )}
            </div>
            {isUserMessage && (
              <Avatar size={32} style={{ backgroundColor: '#F59E0B', flexShrink: 0 }} icon={<UserOutlined />} />
            )}
          </div>
        </div>
      );
    });
  }, [localMessages, selectedExpert, downloadAsMarkdown, downloadAsPDF, formatMessageTime]);

  const handleExpertSelect = async (expert: IAIExpert) => {
    // If expert requires subscription, check access first
    if (expert.requiredSubscriptionLevel) {
      if (!selectedBrandId) {
        message.warning('Please select a brand first to check expert access');
        return;
      }

      try {
        const result = await checkExpertAccess({
          jwtToken: user?.jwtToken || '',
          expertType: expert.expertType,
          brandId: selectedBrandId
        }).unwrap();

        if (!result.hasAccess) {
          // Show upgrade modal
          setUpgradeInfo({
            requiredPlan: result.requiredSubscriptionLevel as SubscriptionPlanEnum,
            currentPlan: result.currentSubscriptionLevel as SubscriptionPlanEnum,
            expertName: expert.name,
          });
          setShowUpgradeModal(true);
          return;
        }
      } catch (error) {
        message.error('Failed to check expert access');
        return;
      }
    }

    // Access granted or no subscription required, proceed with expert selection
    setSelectedExpert(expert);
    setCurrentConversation(null);
    setLocalMessages([]);
    setIsWaitingForResponse(false);
  };

  const handleStartNewConversation = async (contextKey?: string) => {
    if (!selectedExpert) return;

    try {
      const result = await createConversation({
        jwtToken: user?.jwtToken || '',
        payload: { expertType: selectedExpert.expertType, ...(contextKey ? { contextKey } : {}) }
      }).unwrap();

      setCurrentConversation(result.id);
      refetchConversations();
      if (result.existing && (result.messageCount ?? 0) > 0) {
        // Same deep-linked question as before: reopen that conversation and
        // drop the prompt so the composer isn't primed with a question the
        // user already asked.
        setSearchParams({}, { replace: true });
        message.success(intl.formatMessage({
          id: 'aiExperts.conversationReopened',
          defaultMessage: 'Continuing your earlier conversation on this topic',
        }));
      } else {
        message.success('New conversation started!');
      }
    } catch (error) {
      message.error('Failed to start new conversation');
    }
  };

  // URL Parameter Handling: Execute auto-start conversation when flag is set
  useEffect(() => {
    if (pendingAutoStartConversation && selectedExpert && !currentConversation) {
      setPendingAutoStartConversation(false);
      handleStartNewConversation(contextParam ?? undefined);
    }
  }, [pendingAutoStartConversation, selectedExpert, currentConversation]);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedExpert || isWaitingForResponse || isStreaming) return;

    const messageText = messageInput.trim();
    setMessageInput('');

    // Add user message immediately to local state
    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      type: 'user',
      content: messageText,
      createdAt: new Date().toISOString()
    };

    setLocalMessages(prev => [...prev, tempUserMessage]);
    setIsWaitingForResponse(true);

    try {
      // Use streaming chat
      const result = await streamMessage({
        expertType: selectedExpert.expertType,
        message: messageText,
        conversationId: currentConversation || undefined,
        brandId: selectedBrandId,
        jwtToken: user?.jwtToken || '',
        responseStyle,
        scope,
      });

      // If this was the first message, set the conversation ID
      if (result && !currentConversation) {
        setCurrentConversation(result.conversationId);
      }

      // Refresh the conversation data to show persisted messages
      if (result) {
        refetchMessages();
        refetchConversations();
        refetchEntitlements(); // Update credit count
      }
    } catch (error: any) {
      // Check if this is a credits exhausted error
      if (error?.message?.includes('AI_CREDITS_EXHAUSTED')) {
        message.error({
          content: 'You have used all your AI Expert Credits. Purchase more credits to continue.',
          duration: 8,
        });
        refetchEntitlements(); // Update credit count
      } else if (error?.message?.includes('AI_QUOTA_EXCEEDED')) {
        message.error({
          content: 'AI quota exceeded. Your organization has reached its AI token limit. Please contact your administrator to upgrade your plan.',
          duration: 8,
        });
      } else {
        message.error(error?.message || 'Failed to send message');
      }
      setMessageInput(messageText); // Restore the message
      // Remove the temporary message on error
      setLocalMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
    } finally {
      setIsWaitingForResponse(false);
    }
  };

  const handleDeleteConversation = (conversationId: string) => {
    Modal.confirm({
      title: 'Delete Conversation',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this conversation? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteConversation({
            jwtToken: user?.jwtToken || '',
            conversationId
          }).unwrap();

          if (currentConversation === conversationId) {
            setCurrentConversation(null);
          }
          refetchConversations();
          message.success('Conversation deleted');
        } catch (error) {
          message.error('Failed to delete conversation');
        }
      },
    });
  };

  if (expertsError) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <Alert
          message="Error loading AI experts"
          description="Unable to fetch available experts. Please try again later."
          type="error"
          showIcon
          style={{ margin: '16px 0' }}
        />
      </Content>
    );
  }

  // Embedded: while the forced expert is resolving, show a spinner rather than
  // flashing the (skipped) selection grid.
  if (embedded && !selectedExpert) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 96 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Content style={{ margin: embedded ? 0 : '0 16px' }}>
      <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
      {!embedded && (
        <Breadcrumb
          style={{ margin: '16px 0' }}
          items={[
            { title: user.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
            { title: intl.formatMessage({ id: 'aiExpert.breadcrumb.aiExpert' }) }
          ]}
        />
      )}

      <div
        style={{
          padding: 24,
          minHeight: '82vh',
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        {!selectedExpert ? (
          // Expert Selection View
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ marginBottom: 8 }}>
                AI Brand Experts
                <MaturityTag type="earlyAccess" />
              </Title>
              <Paragraph style={{ fontSize: 16, color: '#666', maxWidth: 600, margin: '0 auto' }}>
                Choose from our specialized AI experts to get insights about brand strategy,
                consumer psychology, analytics, and more. Each expert has unique knowledge and perspective.
              </Paragraph>
              <MaturityAlert type="earlyAccess" style={{ marginTop: 16, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }} />
              {hasAiExpertsAccess && creditsLimit > 0 && (
                <div style={{ marginTop: 12 }}>
                  <CreditIndicator
                    remaining={creditsRemaining}
                    limit={creditsLimit}
                    isLow={isLowCredits}
                    isOut={isOutOfCredits}
                    onBuyMore={() => navigate('/dashboard/organisation?tab=billing&subtab=addons')}
                  />
                </div>
              )}
            </div>

            {/* Upgrade banner for users without AI access */}
            {!tierLoading && !hasAiExpertsAccess && (
              <Alert
                type="warning"
                showIcon
                icon={<LockOutlined style={{ color: '#F59E0B' }} />}
                message={
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>{intl.formatMessage({ id: 'subscription.upgrade.aiExpertsRequireUpgrade' })}</span>
                    <Button
                      type="primary"
                      size="small"
                      icon={<CrownOutlined />}
                      onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                      style={{
                        background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                        border: 'none',
                        marginLeft: 16
                      }}
                    >
                      {intl.formatMessage({ id: 'subscription.upgrade.button' })}
                    </Button>
                  </span>
                }
                style={{
                  marginBottom: 16,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                  border: '1px solid #fde68a'
                }}
              />
            )}

            {brands.length > 1 && hasAiExpertsAccess && (
              <div>
                <Space>
                  <span>Select Brand:</span>
                  <Select
                    style={{ width: 200 }}
                    placeholder="Choose a brand"
                    value={selectedBrandId}
                    onChange={setSelectedBrandId}
                  >
                    {brands.map(brand => (
                      <Select.Option key={brand.id} value={brand.id}>
                        {brand.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Space>
              </div>
            )}

            {expertsLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading AI experts...</div>
              </div>
            ) : experts && experts.length > 0 ? (
              <Row gutter={[24, 24]} justify="center">
                {experts.filter(expert => expert.isActive).map((expert) => {
                  // Check both global AI access and per-expert access from API
                  const isLocked = !hasAiExpertsAccess || expert.hasAccess === false;
                  return (
                  <Col xs={24} sm={12} md={8} lg={6} key={expert.id}>
                    <Card
                      hoverable={!isLocked}
                      style={{
                        height: '100%',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.7 : 1,
                        position: 'relative'
                      }}
                      onClick={() => {
                        if (isLocked) {
                          navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
                        } else {
                          handleExpertSelect(expert);
                        }
                      }}
                      styles={{ body: { padding: 20, height: '100%', display: 'flex', flexDirection: 'column' } }}
                    >
                      {isLocked && (
                        <div style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}>
                          <Tag color="default">
                            <LockOutlined style={{ marginRight: 4 }} />
                            {intl.formatMessage({ id: 'subscription.upgrade.required' })}
                          </Tag>
                        </div>
                      )}
                      <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <Avatar
                          size={64}
                          style={{
                            backgroundColor: isLocked ? '#9ca3af' : (colorMap[expert.color] || '#F59E0B'),
                            marginBottom: 12
                          }}
                          icon={iconMap[expert.icon] || <UserOutlined />}
                        />
                        <Title level={4} style={{ marginBottom: 4, color: isLocked ? '#6b7280' : undefined }}>
                          {expert.name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {expert.specialty}
                        </Text>
                        {!isLocked && expert.requiredSubscriptionLevel && (
                          <div style={{ marginTop: 4 }}>
                            <Tag color={expert.requiredSubscriptionLevel === 'enterprise' ? 'gold' : 'blue'}>
                              {expert.requiredSubscriptionLevel === 'enterprise' ? 'Enterprise' : 'Professional'}
                            </Tag>
                          </div>
                        )}
                      </div>

                      <Paragraph 
                        style={{ 
                          fontSize: 13, 
                          textAlign: 'center',
                          flex: 1,
                          marginBottom: 16
                        }}
                      >
                        {expert.description}
                      </Paragraph>

                      <div style={{ textAlign: 'center' }}>
                        <Space size={[4, 4]} wrap style={{ justifyContent: 'center' }}>
                          {expert.expertise.slice(0, 3).map((skill, index) => (
                            <Tag key={index} style={{ margin: 1, fontSize: 11 }}>
                              {skill}
                            </Tag>
                          ))}
                          {expert.expertise.length > 3 && (
                            <Tag style={{ margin: 1, fontSize: 11 }}>
                              +{expert.expertise.length - 3} more
                            </Tag>
                          )}
                        </Space>
                      </div>
                    </Card>
                  </Col>
                  );
                })}
              </Row>
            ) : (
              <Empty 
                description="No AI experts available"
                style={{ margin: '60px 0' }}
              />
            )}
          </Space>
        ) : !currentConversation ? (
          // Expert Info View (Expert selected but no active conversation)
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(82vh - 48px)' }}>
            {/* Header */}
            <div style={{ 
              padding: '16px 0', 
              borderBottom: '1px solid #f0f0f0',
              marginBottom: 24
            }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    {!embedded && (
                      <>
                        <Button
                          icon={<ArrowLeftOutlined />}
                          onClick={() => setSelectedExpert(null)}
                        />
                        <Divider type="vertical" />
                      </>
                    )}
                    <Avatar
                      size="large"
                      style={{ backgroundColor: colorMap[selectedExpert.color] }}
                      icon={iconMap[selectedExpert.icon]}
                    />
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        {selectedExpert.name}
                      </Title>
                      <Text type="secondary">{selectedExpert.specialty}</Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    {creditsLimit > 0 && (
                      <CreditIndicator
                        remaining={creditsRemaining}
                        limit={creditsLimit}
                        isLow={isLowCredits}
                        isOut={isOutOfCredits}
                        onBuyMore={() => navigate('/dashboard/organisation?tab=billing&subtab=addons')}
                      />
                    )}
                    <Button
                      icon={<HistoryOutlined />}
                      onClick={() => setConversationsDrawerOpen(true)}
                    >
                      Past Conversations
                      {conversations && conversations.length > 0 && (
                        <Badge count={conversations.length} style={{ marginLeft: 8 }} />
                      )}
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleStartNewConversation()}
                    >
                      Start New Chat
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>

            {/* Expert Info Content */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '24px 24px 48px 24px',
              overflow: 'auto'
            }}>
              <div style={{ maxWidth: 800, textAlign: 'center', margin: 'auto 0' }}>
                <Avatar
                  size={120}
                  style={{ 
                    backgroundColor: colorMap[selectedExpert.color] || '#F59E0B',
                    marginBottom: 24
                  }}
                  icon={iconMap[selectedExpert.icon] || <UserOutlined />}
                />
                
                <Title level={2} style={{ marginBottom: 8 }}>
                  {selectedExpert.name}
                </Title>
                
                <Title level={4} style={{ color: '#666', fontWeight: 'normal', marginBottom: 24 }}>
                  {selectedExpert.specialty}
                </Title>

                <Paragraph style={{ fontSize: 16, marginBottom: 32, color: '#555' }}>
                  {selectedExpert.description}
                </Paragraph>

                <div style={{ marginBottom: 32 }}>
                  <Title level={5} style={{ marginBottom: 16 }}>Areas of Expertise</Title>
                  <Space size={[8, 8]} wrap style={{ justifyContent: 'center' }}>
                    {selectedExpert.expertise.map((skill, index) => (
                      <Tag key={index} style={{ padding: '4px 12px', fontSize: 13 }}>
                        {skill}
                      </Tag>
                    ))}
                  </Space>
                </div>

                <div style={{ marginBottom: 32 }}>
                  <Space size="large" direction="vertical">
                    <Button
                      type="primary"
                      size="large"
                      icon={<MessageOutlined />}
                      onClick={() => handleStartNewConversation()}
                      style={{ minWidth: 180 }}
                    >
                      Start New Conversation
                    </Button>
                    
                    {conversations && conversations.length > 0 && (
                      <Button
                        size="large"
                        icon={<HistoryOutlined />}
                        onClick={() => setConversationsDrawerOpen(true)}
                        style={{ minWidth: 180 }}
                      >
                        View Past Conversations ({conversations.length})
                      </Button>
                    )}
                  </Space>
                </div>

                <div style={{ 
                  padding: 20, 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 8,
                  border: '1px solid #e9ecef'
                }}>
                  <Title level={5} style={{ marginBottom: 12 }}>
                    💡 How to get the best results
                  </Title>
                  <div style={{ textAlign: 'left' }}>
                    <Text style={{ display: 'block', marginBottom: 8 }}>
                      • Be specific about your brand or product when asking questions
                    </Text>
                    <Text style={{ display: 'block', marginBottom: 8 }}>
                      • Ask about brand performance, comparisons, or insights
                    </Text>
                    <Text style={{ display: 'block', marginBottom: 8 }}>
                      • I can analyze your actual brand equity data in real-time
                    </Text>
                    <Text style={{ display: 'block' }}>
                      • Feel free to ask follow-up questions to dive deeper
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Active Chat Interface View
          <div style={{ height: 'calc(82vh - 48px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ 
              padding: '16px 0', 
              borderBottom: '1px solid #f0f0f0',
              marginBottom: 16
            }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={() => {
                        // Embedded has no grid — back returns to the expert profile.
                        if (!embedded) setSelectedExpert(null);
                        setCurrentConversation(null);
                        setLocalMessages([]);
                      }}
                    />
                    <Divider type="vertical" />
                    <Avatar
                      size="large"
                      style={{ backgroundColor: colorMap[selectedExpert.color] }}
                      icon={iconMap[selectedExpert.icon]}
                    />
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        Chat with {selectedExpert.name}
                      </Title>
                      <Text type="secondary">{selectedExpert.specialty}</Text>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Space>
                    {creditsLimit > 0 && (
                      <CreditIndicator
                        remaining={creditsRemaining}
                        limit={creditsLimit}
                        isLow={isLowCredits}
                        isOut={isOutOfCredits}
                        onBuyMore={() => navigate('/dashboard/organisation?tab=billing&subtab=addons')}
                      />
                    )}
                    <Button
                      icon={<HistoryOutlined />}
                      onClick={() => setConversationsDrawerOpen(true)}
                    >
                      Other Conversations
                      {conversations && conversations.length > 1 && (
                        <Badge count={conversations.length - 1} style={{ marginLeft: 8 }} />
                      )}
                    </Button>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => handleStartNewConversation()}
                    >
                      New Chat
                    </Button>
                  </Space>
                </Col>
              </Row>
            </div>

            {/* Messages Area */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: '0 16px',
              marginBottom: 16,
              width: '100%'
            }}>
              {messagesLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>Loading conversation...</div>
                </div>
              ) : (
                <div style={{ width: '100%' }}>
                  {scopeStampText && (
                    <div style={{ textAlign: 'center', padding: '10px 0 2px' }}>
                      <span
                        style={{
                          fontSize: 12,
                          color: '#94A3B8',
                          background: '#F8FAFC',
                          border: '1px solid #E5E7EB',
                          borderRadius: 999,
                          padding: '3px 12px',
                        }}
                      >
                        {intl.formatMessage(
                          { id: 'aiExperts.scopeStamp', defaultMessage: 'Based on: {label}' },
                          { label: scopeStampText },
                        )}
                      </span>
                    </div>
                  )}
                  {renderedMessages}
                  {(isWaitingForResponse || isStreaming) && selectedExpert && (
                    <div style={{ padding: '8px 0', width: '100%' }}>
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-end',
                        gap: 8
                      }}>
                        <Avatar
                          size={32}
                          style={{
                            backgroundColor: colorMap[selectedExpert.color] || '#F59E0B',
                            flexShrink: 0
                          }}
                          icon={iconMap[selectedExpert.icon] || <RobotOutlined />}
                        />
                        <div style={{
                          maxWidth: '70%',
                          padding: '12px 16px',
                          borderRadius: '12px 12px 12px 4px',
                          backgroundColor: '#f5f5f5',
                        }}>
                          {streamingContent ? (
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                              <ReactMarkdown
                                components={{
                                  h1: ({ children }) => <h1 style={{ fontSize: '1.2em', fontWeight: 'bold', margin: '8px 0 4px 0' }}>{children}</h1>,
                                  h2: ({ children }) => <h2 style={{ fontSize: '1.1em', fontWeight: 'bold', margin: '6px 0 3px 0' }}>{children}</h2>,
                                  h3: ({ children }) => <h3 style={{ fontSize: '1.05em', fontWeight: 'bold', margin: '4px 0 2px 0' }}>{children}</h3>,
                                  p: ({ children }) => <p style={{ margin: '4px 0' }}>{children}</p>,
                                  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
                                  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
                                  li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
                                  strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
                                  em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                  code: ({ children }) => <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>{children}</code>,
                                  blockquote: ({ children }) => <blockquote style={{ borderLeft: '3px solid #ccc', paddingLeft: '12px', margin: '4px 0', fontStyle: 'italic' }}>{children}</blockquote>
                                }}
                              >
                                {streamingContent}
                              </ReactMarkdown>
                              <span style={{
                                display: 'inline-block',
                                width: 8,
                                height: 16,
                                backgroundColor: '#999',
                                marginLeft: 2,
                                animation: 'typing 1s infinite ease-in-out'
                              }} />
                            </div>
                          ) : (
                            <div style={{
                              display: 'flex',
                              gap: 4,
                              alignItems: 'center'
                            }}>
                              <div style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#999',
                                animation: 'typing 1.4s infinite ease-in-out'
                              }} />
                              <div style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#999',
                                animation: 'typing 1.4s infinite ease-in-out',
                                animationDelay: '0.2s'
                              }} />
                              <div style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#999',
                                animation: 'typing 1.4s infinite ease-in-out',
                                animationDelay: '0.4s'
                              }} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area - Only shown when there's an active conversation */}
            <div style={{
              borderTop: '1px solid #f0f0f0',
              paddingTop: 16
            }}>
              <Row gutter={8}>
                <Col flex={1}>
                  <TextArea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder={`Ask ${selectedExpert.name} anything about brands...`}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={!currentConversation}
                  />
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={isWaitingForResponse || isStreaming}
                    disabled={!messageInput.trim() || isWaitingForResponse || isStreaming || !currentConversation}
                    style={{ height: 'auto', minHeight: 32 }}
                  />
                </Col>
              </Row>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Radio.Group
                  size="small"
                  value={responseStyle}
                  onChange={(e) => handleResponseStyleChange(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="brief"><MenuFoldOutlined style={{ marginRight: 4 }} />Brief</Radio.Button>
                  <Radio.Button value="detailed"><MenuUnfoldOutlined style={{ marginRight: 4 }} />Detailed</Radio.Button>
                </Radio.Group>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Press Enter to send, Shift+Enter for new line
                </Text>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conversations Drawer */}
      <Drawer
        title="Conversation History"
        placement="right"
        onClose={() => setConversationsDrawerOpen(false)}
        open={conversationsDrawerOpen}
        width={400}
      >
        {conversationsLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        ) : conversations && conversations.length > 0 ? (
          <div>
            {conversations.map((conversation: IAIConversation) => (
              <div
                key={conversation.id}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  backgroundColor: currentConversation === conversation.id ? '#f0f0f0' : 'transparent',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: 320
                }}
                onClick={() => {
                  // Find and set the expert for this conversation
                  const expert = experts?.find(e => e.expertType === conversation.expertType);
                  if (expert) {
                    setSelectedExpert(expert);
                  }
                  setCurrentConversation(conversation.id);
                  setConversationsDrawerOpen(false);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <MessageOutlined style={{ marginRight: 12, fontSize: 16 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      {conversation.title || `Chat with ${selectedExpert?.name}`}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {conversation.messageCount} {conversation.messageCount === 1 ? 'message' : 'messages'} • {new Date(conversation.createdAt).toLocaleDateString()} {new Date(conversation.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                  </div>
                </div>
                <Tooltip title="Delete conversation">
                  <Button 
                    type="text" 
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteConversation(conversation.id);
                    }}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        ) : (
          <Empty description="No conversations yet" />
        )}
      </Drawer>

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        visible={showUpgradeModal}
        onCancel={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          window.location.href = '/dashboard/organisation?tab=billing';
        }}
        currentPlan={upgradeInfo?.currentPlan || null}
        requiredPlan={upgradeInfo?.requiredPlan || SubscriptionPlanEnum.Professional}
        featureName={`${upgradeInfo?.expertName} Expert`}
        brandName={selectedBrand?.name}
      />
    </Content>
  );
};

export default AIExpertPage;